import { Col, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import Select, { components } from "react-select";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import FixedSave from "../../../../components/Actions/FixedSave";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import i18n from "i18next";
import AppSelect from "../../../../components/Forms/AppSelect";

const CustomerMenuList = (props) => (
  <components.MenuList {...props}>
    {props.children}
    <div
      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
      onClick={() => window.open('/crm/customers/create', '_blank')}
      style={{ padding: '8px 12px', cursor: 'pointer', color: '#0d6efd', borderTop: '1px solid #e0e0e0', fontSize: 14 }}
    >
      + {i18n.t('common.createNewCustomer')}
    </div>
  </components.MenuList>
);

function CreatePaymentPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const urlInvoiceId = searchParams.get('invoice_id');
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [companySettings, setCompanySettings] = useState({});
  const [paymentData, setPaymentData] = useState({
    customer_id: state?.paymentData?.customer_id || '',
    invoice_id: urlInvoiceId || '',
    amount_received: state?.paymentData?.amount_received || '',
    bank_charges: state?.paymentData?.bank_charges || '',
    payment_date: state?.paymentData?.payment_date ? parseISO(state.paymentData.payment_date) : null,
    payment_method: state?.paymentData?.payment_method || '',
    reference: state?.paymentData?.reference || '',
    attachment: null,
    notes: state?.paymentData?.notes || '',
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/sales/payments/create?invoice_id=' + urlInvoiceId);

    if(data.success) {
      setCustomersData(data.customers);
      setCompanySettings(data.company_settings);

      // handle invoice
      if(data.invoice) {
        setInvoiceData(data.invoice);
        if(!paymentData.amount_received) {
          setPaymentData(prev => ({ ...prev, amount_received: data.invoice.total_amount }));
        }
        if(!paymentData.customer_id) {
          setPaymentData(prev => ({ ...prev, customer_id: data.invoice.customer_id }));
        }
        if(!paymentData.payment_date) {
          setPaymentData(prev => ({ ...prev, payment_date: data.invoice.invoice_date ? parseISO(data.invoice.invoice_date) : null }));
        }
      }
      setInitLoading(false);
    }
  }

  const savePayment = async () => {
    setSaveLoading(true);

    const url = id ? '/sales/payments/update/' + id : '/sales/payments/store';
    const data = await post(url, {
      ...paymentData,
      payment_date: paymentData.payment_date ? format(paymentData.payment_date, 'yyyy-MM-dd') : null,
    });

    if(data.success) {
      navigate('/sales/payments');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sales.payments'), id ? t('sales.editPayment') : t('sales.newPayment')]}
          onBack={() => navigate('/sales/payments')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.client')}: </Label>
              <Select
                options={customersData.map(customer => ({ value: customer.id, label: customer.company_name || customer.firstname + ' ' + customer.lastname }))}
                styles={SelectCustomStyles}
                value={customersData.map(c => ({ value: c.id, label: c.company_name || c.firstname + ' ' + c.lastname })).find(o => o.value === paymentData.customer_id) || null}
                onChange={selected => setPaymentData({ ...paymentData, customer_id: selected ? selected.value : null })}
                components={{ MenuList: CustomerMenuList }}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.amountReceived')}</Label>
              <InputGroup>
                <InputGroupText>{ companySettings?.sales?.base_currency }</InputGroupText>
                <Input
                  type="number"
                  value={paymentData.amount_received}
                  onChange={e => setPaymentData({ ...paymentData, amount_received: e.target.value })}
                />
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.bankCharges')}</Label>
              <InputGroup>
                <InputGroupText>{ companySettings?.sales?.base_currency }</InputGroupText>
                <Input
                  type="number"
                  value={paymentData.bank_charges}
                  onChange={e => setPaymentData({ ...paymentData, bank_charges: e.target.value })}
                />
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.paymentDate')}: </Label>
              <DatePicker
                selected={paymentData.payment_date}
                onChange={(d) => setPaymentData({ ...paymentData, payment_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.paymentMode')}: </Label>
              <AppSelect
                options={[
                  { value: 'cash', label: t('sales.cash') },
                  { value: 'credit_card', label: t('accounting.creditCard') },
                  { value: 'bank_transfer', label: t('sales.bankTransfer') },
                ]}
                value={paymentData.payment_method}
                placeholder={t('common.select')}
                isClearable
                onChange={value => setPaymentData({ ...paymentData, payment_method: value })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.reference')}: </Label>
              <Input
                type="text"
                value={paymentData.reference}
                onChange={e => setPaymentData({ ...paymentData, reference: e.target.value })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('attachments.attachment')}</Label>
              <Input
                type="file"
                onChange={e => setPaymentData({ ...paymentData, attachment: e.target.files[0] })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.notes')}</Label>
              <Input
                type="textarea"
                value={paymentData.notes}
                onChange={e => setPaymentData({ ...paymentData, notes: e.target.value })}
              />
            </Col>
          </Row>
        </div>
      </div>

      <FixedSave
        onSave={savePayment}
        loading={saveLoading}
        disabled={saveLoading || !paymentData?.customer_id}
      />
    </SidebarLayout>
  );
}

export default CreatePaymentPage;
